(() => {
  if (customElements.get('subversa-stamp-picker')) return;
  let quickViewSelection = null;
  const absoluteImage = source => source ? new URL(source, window.location.origin).href : '';
  const editData = editor => JSON.parse(editor.querySelector('[data-stamp-cart-edit]')?.textContent || '{}');
  const normalizeProperties = properties => {
    if (!Array.isArray(properties)) return properties;
    const decode = value => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = String(value);
      return textarea.value;
    };
    return Object.fromEntries(properties.map(([key, value]) => [decode(key), decode(value)]));
  };
  const editProperties = (editor, variantId, original) => {
    original = normalizeProperties(original);
    if (!original?._stamp_id) return original;
    const properties = { ...original };
    const data = editData(editor);
    const stamp = data.stamps?.[properties._stamp_id];
    const variant = data.variants?.[variantId];
    if (stamp && variant) {
      properties._stamp_image = absoluteImage(stamp[variant.style] || stamp.default);
    } else if (String(properties._stamp_image_variant) !== String(variantId)) {
      properties._stamp_image = '';
    }
    properties._stamp_image_variant = String(variantId);
    return properties;
  };
  const updateEditor = editor => {
    if (!editor) return;
    const original = normalizeProperties(JSON.parse(editor.getAttribute('data-stamp-properties') || 'null'));
    if (!original?._stamp_id) return;
    const data = editData(editor);
    editor.querySelectorAll('.product-edit-item').forEach(item => {
      const variantId = item.querySelector('[name="id"]')?.value;
      const properties = editProperties(editor, variantId, original);
      const source = properties._stamp_image || data.variants?.[variantId]?.image || '';
      const image = item.querySelector('.product-edit-image img');
      if (!image) return;
      image.hidden = !source;
      if (source) {
        image.src = absoluteImage(source);
        image.removeAttribute('srcset');
        image.style.objectFit = 'contain';
        image.closest('.product-edit-image').style.paddingBottom = '100%';
      }
    });
  };
  window.SubversaStampCart = { propertiesForVariant: editProperties, updateEditor };
  document.addEventListener('change', event => {
    const editor = event.target.closest('[data-template-cart-edit]');
    if (editor) queueMicrotask(() => updateEditor(editor));
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-open-quick-view-popup]');
    if (trigger) {
      quickViewSelection = {
        handle: trigger.dataset.productHandle,
        stamp: trigger.closest('[data-stamp-card]')?.dataset.stampCard || null
      };
    }
    const quickShop = event.target.closest('[data-quickshop-popup], [data-open-quickshop-popup-list]');
    const card = quickShop?.closest('[data-stamp-card]');
    if (card) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(card.dataset.stampUrl);
    }
  }, true);

  class StampPicker extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.select = this.querySelector('[data-stamp-select]');
      if (!this.select?.options.length) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.scope = this.closest('.productView') || this.closest('form');
      this.variantStyles = JSON.parse(this.querySelector('[data-stamp-variants]')?.textContent || '{}');
      this.isQuickView = !!this.closest('.quickView');
      const initial = this.isQuickView
        ? (quickViewSelection?.handle === this.dataset.productHandle ? quickViewSelection.stamp : null)
        : new URL(window.location.href).searchParams.get('stamp');
      if ([...this.select.options].some(option => option.value === initial)) this.select.value = initial;
      this.selectedHandle = this.select.value;
      this.scope.addEventListener('change', () => {
        queueMicrotask(() => this.sync(!this.isQuickView));
      }, options);
      if (!this.isQuickView) {
        document.querySelectorAll('variant-sticky-selects, variant-sticky-radios').forEach(sticky => {
          sticky.addEventListener('change', () => queueMicrotask(() => this.sync(true)), options);
        });
      }
      this.scope.addEventListener('click', event => {
        if (event.target.closest('[data-btn-addtocart], .shopify-payment-button')) this.sync(false);
      }, { ...options, capture: true });
      this.scope.addEventListener('submit', () => this.sync(false), { ...options, capture: true });
      window.addEventListener('popstate', () => {
        if (this.isQuickView) return;
        const handle = new URL(window.location.href).searchParams.get('stamp');
        this.select.value = [...this.select.options].some(option => option.value === handle)
          ? handle : this.select.options[0].value;
        this.selectedHandle = this.select.value;
        this.sync(false);
      }, options);
      this.galleryObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-stamp-media]') || node.querySelector('[data-stamp-media]'))))) this.sync(false);
      });
      const gallery = this.scope.querySelector('.productView-images');
      if (gallery) this.galleryObserver.observe(gallery, { childList: true, subtree: true });
      this.sync(false);
    }

    sync(updateURL) {
      this.select.value = this.selectedHandle;
      const option = this.select.selectedOptions[0];
      if (!option) return;
      const forms = [...this.scope.querySelectorAll('form')];
      if (this.scope.matches('form')) forms.push(this.scope);
      if (!this.isQuickView) {
        const sticky = document.getElementById(`product-form-sticky-${this.dataset.productId}`);
        if (sticky && !forms.includes(sticky)) forms.push(sticky);
      }
      forms.forEach(form => {
        form.querySelectorAll('[data-stamp-property="id"]').forEach(input => { input.value = option.dataset.stampId; });
        form.querySelectorAll('[data-stamp-property="name"]').forEach(input => { input.value = option.textContent; });
      });
      const variantInput = this.closest('form')?.querySelector('[name="id"]');
      const variantControl = this.scope.querySelector('variant-radios, variant-selects, variant-quick-view-radios, variant-quick-view-selects');
      const variantId = variantControl && 'currentVariant' in variantControl
        ? variantControl.currentVariant?.id : variantInput?.value;
      const style = (this.variantStyles[variantId] || '_').trim();
      const color = style.split('_')[0];
      const source = option.getAttribute(`data-image-${color}`) || option.dataset.stampImage;
      const mockup = option.getAttribute(`data-t-shirt-image-${style.replace('_', '-')}`) || option.dataset.tShirtImage;
      forms.forEach(form => {
        form.querySelectorAll('[data-stamp-property="image"]').forEach(input => { input.value = absoluteImage(mockup); });
        form.querySelectorAll('[data-stamp-property="image-variant"]').forEach(input => { input.value = variantId || ''; });
      });
      this.updateGallery({ mockup, artwork: source }, option.textContent);
      if (updateURL && window.location.pathname.endsWith(`/products/${this.dataset.productHandle}`)) {
        const url = new URL(window.location.href);
        url.searchParams.set('stamp', option.value);
        window.history.replaceState(window.history.state, '', url);
      }
    }

    updateGallery(sources, name) {
      const $ = window.jQuery;
      const sliders = [...this.scope.querySelectorAll('.productView-nav, .productView-for')];
      const media = new Set(this.scope.querySelectorAll('[data-stamp-media]'));
      sliders.forEach(slider => {
        if ($ && slider.classList.contains('slick-initialized')) {
          const slick = $(slider).slick('getSlick');
          slick.$slidesCache?.find('[data-stamp-media]').addBack('[data-stamp-media]').each((_, node) => media.add(node));
        }
      });
      media.forEach(node => {
        const source = sources[node.dataset.stampMedia];
        const image = node.querySelector('img:not(.zoomImg)');
        if (!image) return;
        const thumbnail = node.classList.contains('productView-thumbnail');
        const url = source && (thumbnail ? source.replace('width=1600', 'width=180') : source);
        node.toggleAttribute('data-stamp-empty', !source);
        image.hidden = !source;
        image.alt = `${this.dataset.productTitle || ''} — ${name}`;
        if (url && image.getAttribute('src') !== url) {
          image.removeAttribute('srcset');
          if (!thumbnail) image.srcset = [360, 720, 1100, 1600].map(width => `${source.replace('width=1600', `width=${width}`)} ${width}w`).join(', ');
          image.src = url;
          image.onload = () => {
            if (!thumbnail && image.naturalWidth) node.querySelector('.productView-img-container').style.paddingBottom = `${image.naturalHeight / image.naturalWidth * 100}%`;
          };
        }
        const link = node.querySelector('[data-stamp-zoom], .productView-thumbnail-link');
        if (!link) return;
        if (thumbnail) {
          if (source) link.dataset.image = source;
          else delete link.dataset.image;
          if ($) $(link).removeData('image');
        } else {
          const full = source?.replace('width=1600', 'width=2400');
          const changed = link.getAttribute('href') !== full;
          if (full) link.setAttribute('href', full);
          else link.removeAttribute('href');
          if ($) $(link).removeData('src');
          if (changed && link.hasAttribute('data-zoom-image')) {
            if ($) $(link).trigger('zoom.destroy');
            if (full) {
              link.dataset.zoomImage = full;
              if ($?.fn.zoom && window.innerWidth > 1024) $(link).zoom({ url: full, touch: false });
            }
          }
        }
      });
      const signature = `${!!sources.mockup}:${!!sources.artwork}`;
      sliders.forEach(slider => {
        if (!$ || !slider.classList.contains('slick-initialized') || slider.dataset.stampVisibility === signature) return;
        slider.dataset.stampVisibility = signature;
        if (slider.dataset.stampFiltered) $(slider).slick('slickUnfilter');
        if (!sources.mockup || !sources.artwork) {
          $(slider).slick('slickFilter', function() { return !this.hasAttribute('data-stamp-empty'); });
          slider.dataset.stampFiltered = 'true';
        } else delete slider.dataset.stampFiltered;
      });
    }

    disconnectedCallback() {
      this.galleryObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('subversa-stamp-picker', StampPicker);
})();
